import { formatMonthName, formatSEK } from '@budgie/shared';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { cn } from '@/lib/utils';
import { type RouterOutputs, trpc } from '../../lib/trpc';
import { EmptyState } from '../common/EmptyState';
import { TableSkeleton } from '../common/Skeleton';

type Props = {
  billId: string;
};

type HistoryRow = RouterOutputs['recurringBill']['history'][number];
type HistoryTransactions = HistoryRow['transactions'];

export function TransactionDetails({ transactions }: { transactions: HistoryTransactions }) {
  if (transactions.length === 0) {
    return <span className="text-sm text-muted-foreground/70">Ingen betalning</span>;
  }

  return (
    <details>
      <summary className="flex min-h-11 cursor-pointer items-center text-sm font-medium text-primary md:min-h-0">
        Visa transaktioner
      </summary>
      <div className="mt-2 space-y-2 rounded-lg border border-border bg-muted/20 p-3">
        {transactions.map((transaction) => (
          <div key={transaction.id} className="flex items-center justify-between gap-3 text-sm">
            <div className="min-w-0">
              <div className="break-words font-medium text-foreground">
                {transaction.description}
              </div>
              <div className="text-xs text-muted-foreground">
                {transaction.date}
                {transaction.accountName ? ` · ${transaction.accountName}` : ''}
              </div>
            </div>
            <div className="shrink-0 tabular-nums text-foreground">
              {formatSEK(transaction.amount)}
            </div>
          </div>
        ))}
      </div>
    </details>
  );
}

/**
 * One row per budget period (newest first): amount, payment count and the
 * matched transactions. Shared by Räkningar and Kostnadshistorik.
 */
export function BillHistoryPeriodList({
  rows,
  className,
  describePeriod,
}: {
  rows: HistoryRow[];
  className?: string;
  /** Extra text after the payment count, e.g. the period's date range */
  describePeriod?: (year: number, month: number) => string | undefined;
}) {
  return (
    <ul className={cn('divide-y divide-border rounded-lg border border-border', className)}>
      {rows.map((row) => {
        const extra = describePeriod?.(row.year, row.month);
        return (
          <li key={`${row.year}-${row.month}`} className="space-y-1 px-3 py-3">
            <div className="flex items-baseline justify-between gap-3">
              <span className="font-medium capitalize text-foreground">
                {formatMonthName(row.month)} {row.year}
              </span>
              <span
                className={cn(
                  'tabular-nums',
                  row.paymentCount > 0 ? 'text-foreground' : 'text-muted-foreground',
                )}
              >
                {row.paymentCount > 0 ? formatSEK(row.totalAmount) : '–'}
              </span>
            </div>
            <div className="text-xs text-muted-foreground">
              {row.paymentCount} {row.paymentCount === 1 ? 'betalning' : 'betalningar'}
              {extra ? ` · ${extra}` : ''}
            </div>
            {row.paymentCount > 0 && <TransactionDetails transactions={row.transactions} />}
          </li>
        );
      })}
    </ul>
  );
}

export function BillHistory({ billId }: Props) {
  const query = trpc.recurringBill.history.useQuery({ billId, months: 12 }, { retry: false });

  if (query.isLoading) {
    return <TableSkeleton rows={6} columns={4} />;
  }

  if (query.error) {
    return (
      <EmptyState
        icon="⚠️"
        title="Kunde inte hämta historik"
        description="Försök igen om en liten stund. Historiken har inte gått förlorad, men den kunde inte laddas just nu."
      />
    );
  }

  const rows = query.data ?? [];
  const hasPayments = rows.some((row) => row.paymentCount > 0);

  if (!hasPayments) {
    return (
      <EmptyState
        icon="🗓️"
        title="Ingen betalningshistorik"
        description="När räkningen har matchade transaktioner visas historiken här."
      />
    );
  }

  return (
    <Card size="sm">
      <CardHeader>
        <CardTitle className="text-sm font-semibold">Historik</CardTitle>
      </CardHeader>
      <CardContent>
        <BillHistoryPeriodList rows={rows} className="md:hidden" />

        <div className="hidden overflow-x-auto rounded-lg border border-border md:block">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Månad</TableHead>
                <TableHead className="text-right">Faktiskt belopp</TableHead>
                <TableHead className="text-right">Betalningar</TableHead>
                <TableHead>Detaljer</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={`${row.year}-${row.month}`}>
                  <TableCell className="capitalize">
                    {formatMonthName(row.month)} {row.year}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {formatSEK(row.totalAmount)}
                  </TableCell>
                  <TableCell className="text-right text-muted-foreground">
                    {row.paymentCount}
                  </TableCell>
                  <TableCell>
                    <TransactionDetails transactions={row.transactions} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}
